class Searcher {
    constructor(config = {}) {
        this.options = {
            input: '[data-searcher-input]',
            button: '[data-searcher-button]',
            clear: '[data-searcher-clear]',
            wrapper: '[data-searcher-wrapper]',
            suggests: '[data-searcher-suggests]',
            populars: '[data-searcher-popular]',
            middle: '[data-middle-block]',
            plate: '[data-search-plate]',
            apiUrl: config.apiUrl,
            apiPopulars: config.apiPopulars,
            debounceDelay: config.debounceDelay || 220,
            popularsDelay: config.popularsDelay || 2000,
            maxTextareaHeight: config.maxTextareaHeight || 97,
            debug: config.debug ?? false,
            ...config
        };

        this.elements = {
            inputElement: document.querySelector(this.options.input),
            buttonElement: document.querySelector(this.options.button),
            clearElement: document.querySelector(this.options.clear),
            wrapperElement: document.querySelector(this.options.wrapper),
            suggestsElement: document.querySelector(this.options.suggests),
            popularsElement: document.querySelector(this.options.populars),
            middleElement: document.querySelector(this.options.middle),
            plateElement: document.querySelector(this.options.plate),
        };

        // Проверяем наличие обязательных элементов
        if (!this.elements.inputElement) {
            this.warn('input element not found');
            return;
        }

        this.popularsTimer = null;
        this.scrollLockHandlers = null;
        this.selectedSuggestIndex = -1; // Индекс выбранной подсказки (-1 = ничего не выбрано)

        // Bound methods for cleanup
        this.boundHandlers = {
            input: null,
            keydown: null,
            clearClick: null,
            plateClick: null,
            focus: null,
            blur: null,
            resize: null,
            buttonClick: null
        };

        this.init();
    }

    /**
     * Debounce utility
     */
    debounce(fn, delay) {
        let timer;
        return (...args) => {
            clearTimeout(timer);
            timer = setTimeout(() => fn.apply(this, args), delay);
        };
    }

    /**
     * Resize textarea and toggle clear button
     */
    resizeAndToggleClear() {
        const input = this.elements.inputElement;
        if (!input) return;

        // Normalize multiple spaces
        input.value = input.value.replace(/ +/g, " ");

        // Auto-resize textarea
        input.style.height = 'auto';
        if (input.scrollHeight < this.options.maxTextareaHeight) {
            input.style.height = input.scrollHeight + 'px';
            input.style.overflowY = 'hidden';
        } else {
            input.style.height = this.options.maxTextareaHeight + 'px';
            input.style.overflowY = 'auto';
        }

        // Toggle clear button
        if (this.elements.clearElement) {
            if (input.value.trim().length > 0) {
                this.elements.clearElement.classList.add('_active');
            } else {
                this.elements.clearElement.classList.remove('_active');
                this.resetPopularsTimer();
            }
        }
    }

    /**
     * Инициализация компонента
     */
    init() {
        const input = this.elements.inputElement;

        // ARIA-атрибуты для доступности
        input.setAttribute('role', 'combobox');
        input.setAttribute('aria-autocomplete', 'list');
        input.setAttribute('aria-expanded', 'false');
        if (this.elements.suggestsElement) {
            const suggestsId = 'search-suggests-' + Math.random().toString(36).slice(2, 9);
            this.elements.suggestsElement.id = suggestsId;
            input.setAttribute('aria-controls', suggestsId);
        }

        // Клик по плашке — фокус на поле ввода (но не при клике на кнопку)
        if (this.elements.plateElement && this.elements.wrapperElement && this.elements.middleElement) {
            this.boundHandlers.plateClick = (e) => {
                // Не фокусируем input если клик был на кнопке
                if (this.elements.buttonElement && (e.target === this.elements.buttonElement || this.elements.buttonElement.contains(e.target))) {
                    return;
                }
                // focus handler сам добавит классы и сделает scrollTo
                input.focus();
            };
            this.elements.plateElement.addEventListener('click', this.boundHandlers.plateClick);
        }

        // Обработчик ввода
        this.boundHandlers.input = () => {
            this.resizeAndToggleClear();
            this.selectedSuggestIndex = -1; // Сброс выбранной подсказки при новом вводе
            this.debouncedSuggest(input.value.trim());
            this.resetPopularsTimer();
            // Убираем состояние "искали" при изменении текста
            if (this.elements.buttonElement) {
                this.elements.buttonElement.classList.remove('_searched');
            }
        };
        input.addEventListener('input', this.boundHandlers.input);

        // Обработчик клавиатуры (Enter, стрелки, Escape)
        this.boundHandlers.keydown = (e) => {
            const suggests = this.elements.suggestsElement?.querySelectorAll('li');
            const hasSuggests = suggests && suggests.length > 0;

            switch (e.key) {
                case 'ArrowDown':
                    if (hasSuggests) {
                        e.preventDefault();
                        this.selectedSuggestIndex = Math.min(
                            this.selectedSuggestIndex + 1,
                            suggests.length - 1
                        );
                        this.highlightSuggest(suggests);
                    }
                    break;

                case 'ArrowUp':
                    if (hasSuggests) {
                        e.preventDefault();
                        this.selectedSuggestIndex = Math.max(this.selectedSuggestIndex - 1, -1);
                        this.highlightSuggest(suggests);
                    }
                    break;

                case 'Escape':
                    if (hasSuggests) {
                        e.preventDefault();
                        this.clearSuggests();
                        this.clearPopulars();
                    }
                    break;

                case 'Enter':
                    if (!e.shiftKey) {
                        e.preventDefault();
                        // Если выбрана подсказка — используем её
                        if (hasSuggests && this.selectedSuggestIndex >= 0) {
                            const selected = suggests[this.selectedSuggestIndex];
                            input.value = selected.textContent || selected.innerText;
                            this.resizeAndToggleClear();
                        }
                        this.clearSuggests();
                        this.clearPopulars();
                        if (this.elements.buttonElement) {
                            this.elements.buttonElement.click();
                        }
                    }
                    break;
            }
        };
        input.addEventListener('keydown', this.boundHandlers.keydown);

        // Clear button handler
        if (this.elements.clearElement) {
            this.boundHandlers.clearClick = () => {
                input.focus();
                this.clearSuggests();
                input.value = '';
                this.resizeAndToggleClear();
            };
            this.elements.clearElement.addEventListener('click', this.boundHandlers.clearClick);
        }

        // Debounced suggest
        this.debouncedSuggest = this.debounce(
            this.fetchSuggests.bind(this),
            this.options.debounceDelay
        );

        // Focus/blur handlers
        if (this.elements.wrapperElement && this.elements.middleElement) {
            this.boundHandlers.focus = () => {
                this.elements.wrapperElement.classList.add('_focused');
                if (this.isMobileUserAgent()) {
                    this.elements.middleElement.classList.add('_mobile');

                    // Простой фикс для iOS - скролл вверх с повторами
                    window.scrollTo(0, 0);
                    setTimeout(() => window.scrollTo(0, 0), 100);
                    setTimeout(() => window.scrollTo(0, 0), 300);
                    setTimeout(() => window.scrollTo(0, 0), 500);
                }
            };

            this.boundHandlers.blur = () => {
                this.elements.wrapperElement.classList.remove('_focused');
            };

            input.addEventListener('focus', this.boundHandlers.focus);
            input.addEventListener('blur', this.boundHandlers.blur);
        }

        // Auto-focus on desktop (but not if there's already a search query)
        const urlParams = new URLSearchParams(window.location.search);
        const hasQuery = urlParams.get('q');
        if (!this.isMobileUserAgent() && !hasQuery) {
            input.focus();
        }

        // Resize handler
        this.debouncedResize = this.debounce(this.resizeAndToggleClear.bind(this), 200);
        this.boundHandlers.resize = this.debouncedResize;
        window.addEventListener("resize", this.boundHandlers.resize);

        // Scroll lock for mobile inputs (prevents viewport jumping on mobile)
        this.initScrollLock();

        // Start populars timer
        this.resetPopularsTimer();

        // Button click handler - show loading state
        if (this.elements.buttonElement && this.elements.plateElement) {
            this.boundHandlers.buttonClick = () => {
                this.elements.plateElement.classList.add('_loading');
                // Hide suggestions and populars when searching
                this.clearSuggests();
                this.clearPopulars();
                // Blur input and restore scroll (bug fix #2)
                if (this.elements.inputElement) {
                    this.elements.inputElement.blur();
                    // Force restore scroll immediately
                    setTimeout(() => {
                        document.documentElement.style.overflow = '';
                    }, 100);
                }
                // Add searched state to button (bug fix #3)
                this.elements.buttonElement.classList.add('_searched');
            };
            this.elements.buttonElement.addEventListener('click', this.boundHandlers.buttonClick);
        }

        // Initialize button states if input has value (after page reload)
        if (input.value.trim()) {
            this.resizeAndToggleClear();
        }
    }

    /**
     * Remove loading state from plate
     */
    removeLoadingState() {
        if (this.elements.plateElement) {
            this.elements.plateElement.classList.remove('_loading');
        }
    }

    /**
     * Check if user agent is mobile
     */
    isMobileUserAgent() {
        return (
            (navigator.userAgentData && navigator.userAgentData.mobile) ||
            /Android|iPhone|iPad|iPod|Opera Mini|IEMobile/i.test(navigator.userAgent) ||
            window.innerWidth <= 768
        );
    }

    /**
     * Initialize scroll lock for inputs (mobile-specific fix)
     * Only locks scroll for inputs within the searcher component
     */
    initScrollLock() {
        // Only get inputs within the wrapper element to avoid global scope
        const wrapper = this.elements.wrapperElement || document.body;
        const inputs = wrapper.querySelectorAll('input, textarea, [contenteditable="true"]');

        let savedY = 0;
        let rafId = null;
        let vvHandler = null;

        const startLock = () => {
            // На главной странице (не результаты) всегда держим скролл на 0
            const isMainPage = !document.body.classList.contains('_results') &&
                !document.querySelector('.mainpage_results');

            if (isMainPage) {
                savedY = 0;
            } else {
                savedY = window.scrollY || window.pageYOffset;
            }

            // Принудительно возвращаем к нужной позиции
            window.scrollTo(0, savedY);

            if (window.visualViewport) {
                vvHandler = () => window.scrollTo(0, savedY);
                window.visualViewport.addEventListener('resize', vvHandler);
            } else {
                const loop = () => {
                    window.scrollTo(0, savedY);
                    rafId = requestAnimationFrame(loop);
                };
                loop();
            }
            document.documentElement.style.overflow = 'hidden';

            // Дополнительные попытки для iOS (клавиатура появляется с задержкой)
            setTimeout(() => window.scrollTo(0, savedY), 100);
            setTimeout(() => window.scrollTo(0, savedY), 300);
        };

        const stopLock = () => {
            if (window.visualViewport && vvHandler) {
                window.visualViewport.removeEventListener('resize', vvHandler);
                vvHandler = null;
            }
            if (rafId) {
                cancelAnimationFrame(rafId);
                rafId = null;
            }
            document.documentElement.style.overflow = '';
            window.scrollTo(0, savedY);
        };

        inputs.forEach(el => {
            el.addEventListener('focus', startLock);
            el.addEventListener('blur', stopLock);
        });

        // Store for cleanup
        this.scrollLockHandlers = { inputs, startLock, stopLock };
    }

    /**
     * Reset populars timer
     */
    resetPopularsTimer() {
        this.clearPopularsTimer();
        if (this.elements.inputElement && this.elements.inputElement.value.trim() === '') {
            this.popularsTimer = setTimeout(() => {
                this.fetchPopulars();
            }, this.options.popularsDelay);
        } else {
            this.clearPopulars();
        }
    }

    /**
     * Clear populars timer
     */
    clearPopularsTimer() {
        if (this.popularsTimer) {
            clearTimeout(this.popularsTimer);
            this.popularsTimer = null;
        }
    }

    /**
     * Fetch popular search terms
     */
    async fetchPopulars() {
        try {
            const response = await fetch(this.options.apiPopulars);
            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }
            const data = await response.json();

            if (Array.isArray(data) && data.length > 0) {
                this.renderPopulars(data.slice(0, 20));
            } else {
                this.clearPopulars();
            }
        } catch (err) {
            this.error('Ошибка загрузки популярных запросов:', err);
            this.clearPopulars();
        }
    }

    /**
     * Render popular tags
     */
    renderPopulars(tags) {
        if (!this.elements.popularsElement) return;

        this.elements.popularsElement.innerHTML = tags
            .map(t => `<li>${this.formatSuggestion(t)}</li>`)
            .join('');

        if (this.elements.middleElement) {
            this.elements.middleElement.classList.add('_populars');
        }

        // Handle tag click (use mousedown to prevent blur from clearing tags)
        this.elements.popularsElement.querySelectorAll('li').forEach(li => {
            li.addEventListener('mousedown', (e) => {
                e.preventDefault();
                if (this.elements.inputElement) {
                    this.elements.inputElement.value = li.textContent || li.innerText;
                    this.resizeAndToggleClear();
                }
                this.clearPopulars();
                this.clearSuggests();
                if (this.elements.buttonElement) {
                    this.elements.buttonElement.click();
                }
            });
        });
    }

    /**
     * Clear popular tags
     */
    clearPopulars() {
        if (this.elements.popularsElement) {
            this.elements.popularsElement.innerHTML = '';
        }
        if (this.elements.middleElement) {
            this.elements.middleElement.classList.remove('_populars');
        }
    }

    /**
     * Fetch search suggestions
     */
    async fetchSuggests(query) {
        // Don't show suggests if user already searched (button has _searched class)
        if (this.elements.buttonElement?.classList.contains('_searched')) {
            return;
        }

        if (!query) {
            this.clearSuggests();
            return;
        }

        try {
            const response = await fetch(`${this.options.apiUrl}?q=${encodeURIComponent(query)}`);
            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }
            const data = await response.json();

            // Check again after fetch - user may have clicked "Найти" while waiting
            if (this.elements.buttonElement?.classList.contains('_searched')) {
                return;
            }

            if (Array.isArray(data) && data.length > 0) {
                this.renderSuggests(data);
            } else {
                this.clearSuggests();
            }
        } catch (err) {
            this.error('Ошибка загрузки подсказок:', err);
            this.clearSuggests();
        }
    }

    /**
     * Рендер подсказок поиска
     */
    renderSuggests(suggests) {
        if (!this.elements.suggestsElement || !this.elements.wrapperElement) return;

        // Сбрасываем выбор при новом рендере
        this.selectedSuggestIndex = -1;

        const rect = this.elements.wrapperElement.getBoundingClientRect();
        const availablePx = window.innerHeight - rect.bottom - 100;
        const maxCount = Math.max(1, Math.floor(availablePx / 56));
        const visibleSuggests = suggests.slice(0, maxCount);

        // Добавляем ARIA-атрибуты для доступности
        this.elements.suggestsElement.setAttribute('role', 'listbox');
        this.elements.inputElement?.setAttribute('aria-expanded', 'true');
        this.elements.suggestsElement.innerHTML = visibleSuggests
            .map((s, i) => `<li role="option" aria-selected="false" tabindex="-1">${this.formatSuggestion(s)}</li>`)
            .join('');

        this.elements.wrapperElement.classList.add('_suggests');
        if (this.elements.middleElement) {
            this.elements.middleElement.classList.add('_suggests');
        }

        // Клик по подсказке (mousedown чтобы blur не очистил список)
        this.elements.suggestsElement.querySelectorAll('li').forEach(li => {
            li.addEventListener('mousedown', (e) => {
                e.preventDefault();
                if (this.elements.inputElement) {
                    this.elements.inputElement.value = li.textContent || li.innerText;
                    this.resizeAndToggleClear();
                }
                this.clearSuggests();
                this.clearPopulars();
                if (this.elements.buttonElement) {
                    this.elements.buttonElement.click();
                }
            });
        });
    }

    /**
     * Очистка подсказок
     */
    clearSuggests() {
        this.selectedSuggestIndex = -1;
        this.elements.inputElement?.setAttribute('aria-expanded', 'false');
        if (this.elements.suggestsElement) {
            this.elements.suggestsElement.innerHTML = '';
        }
        if (this.elements.wrapperElement) {
            this.elements.wrapperElement.classList.remove('_suggests');
        }
        if (this.elements.middleElement) {
            this.elements.middleElement.classList.remove('_suggests');
        }
    }

    /**
     * Подсветка выбранной подсказки
     */
    highlightSuggest(suggests) {
        suggests.forEach((li, index) => {
            if (index === this.selectedSuggestIndex) {
                li.classList.add('_selected');
                li.setAttribute('aria-selected', 'true');
            } else {
                li.classList.remove('_selected');
                li.setAttribute('aria-selected', 'false');
            }
        });
    }

    /**
     * Escape HTML to prevent XSS
     */
    escapeHtml(text) {
        const div = document.createElement('div');
        div.textContent = text;
        return div.innerHTML;
    }

    /**
     * Format suggestion text: highlight text in backticks with <b> tags
     * Example: "search `example` query" -> "search <b>example</b> query"
     * @param {string} text - Text to format
     * @returns {string} - Formatted HTML string (XSS-safe)
     */
    formatSuggestion(text) {
        // Split by backticks while keeping them in results
        const parts = text.split(/(`[^`]*`)/g);

        return parts.map(part => {
            // Check if this part is in backticks
            if (part.startsWith('`') && part.endsWith('`')) {
                // Remove backticks and wrap in <b>
                const content = part.slice(1, -1);
                return `<b>${this.escapeHtml(content)}</b>`;
            } else {
                // Regular text - just escape
                return this.escapeHtml(part);
            }
        }).join('');
    }

    /**
     * Cleanup method to remove all event listeners
     */
    destroy() {
        const input = this.elements.inputElement;
        if (!input) return;

        // Remove all event listeners
        if (this.boundHandlers.input) {
            input.removeEventListener('input', this.boundHandlers.input);
        }
        if (this.boundHandlers.keydown) {
            input.removeEventListener('keydown', this.boundHandlers.keydown);
        }
        if (this.boundHandlers.focus) {
            input.removeEventListener('focus', this.boundHandlers.focus);
        }
        if (this.boundHandlers.blur) {
            input.removeEventListener('blur', this.boundHandlers.blur);
        }

        if (this.elements.clearElement && this.boundHandlers.clearClick) {
            this.elements.clearElement.removeEventListener('click', this.boundHandlers.clearClick);
        }

        if (this.elements.plateElement && this.boundHandlers.plateClick) {
            this.elements.plateElement.removeEventListener('click', this.boundHandlers.plateClick);
        }

        if (this.elements.buttonElement && this.boundHandlers.buttonClick) {
            this.elements.buttonElement.removeEventListener('click', this.boundHandlers.buttonClick);
        }

        if (this.boundHandlers.resize) {
            window.removeEventListener('resize', this.boundHandlers.resize);
        }

        // Remove scroll lock handlers
        if (this.scrollLockHandlers) {
            const { inputs, startLock, stopLock } = this.scrollLockHandlers;
            inputs.forEach(el => {
                el.removeEventListener('focus', startLock);
                el.removeEventListener('blur', stopLock);
            });
        }

        // Clear timers
        this.clearPopularsTimer();

        // Clear UI
        this.clearSuggests();
        this.clearPopulars();
    }

    /**
     * Логирование (только если debug: true)
     */
    log(...args) {
        if (this.options.debug) {
            console.log('[Searcher]', ...args);
        }
    }

    warn(...args) {
        if (this.options.debug) {
            console.warn('[Searcher]', ...args);
        }
    }

    error(...args) {
        if (this.options.debug) {
            console.error('[Searcher]', ...args);
        }
    }
}
export default Searcher;